import { splitIntoRows } from "../splitCircuit";
import type { RouteWaypoint } from "../../../ReviewPage/hooks/useCircuitCablePaths/types";
import type {
  RoomSplitDevice,
  RoomSplitGroup,
  RoomSplitRoom,
  SplitByRooms,
} from "./types";

// A door's middle this close to a room's edge opens into it.
const DOOR_REACH_M = 0.6;
// A room is divided only when it alone holds more than this many circuits' worth.
const ROOM_DIVIDES_ABOVE = 2;
// German and English: an English-speaking user sees the English names of detected rooms.
const WET_ROOM =
  /\b(wc|bad|dusche|duschen|sanit[äa]r|waschraum|toilette|toilets?|bathrooms?|showers?|washrooms?|sanitary)\b/i;

const distance = (a: RouteWaypoint, b: RouteWaypoint): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

const isInside = (at: RouteWaypoint, ring: RouteWaypoint[]): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [a, b] = [ring[i], ring[j]];
    const crosses = a.y > at.y !== b.y > at.y;
    if (crosses && at.x < ((b.x - a.x) * (at.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
};

const distanceToSegment = (
  at: RouteWaypoint,
  a: RouteWaypoint,
  b: RouteWaypoint,
): number => {
  const lengthSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  if (lengthSq === 0) return distance(at, a);
  const t = Math.max(
    0,
    Math.min(
      1,
      ((at.x - a.x) * (b.x - a.x) + (at.y - a.y) * (b.y - a.y)) / lengthSq,
    ),
  );
  return distance(at, { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) });
};

const distanceToRoom = (at: RouteWaypoint, room: RoomSplitRoom): number => {
  if (isInside(at, room.points)) return 0;
  return room.points.reduce(
    (best, a, i) =>
      Math.min(
        best,
        distanceToSegment(at, a, room.points[(i + 1) % room.points.length]),
      ),
    Number.POSITIVE_INFINITY,
  );
};

const centreOf = (room: RoomSplitRoom): RouteWaypoint => ({
  x: room.points.reduce((sum, at) => sum + at.x, 0) / room.points.length,
  y: room.points.reduce((sum, at) => sum + at.y, 0) / room.points.length,
});

// A device on the wall line, or just outside the outline, belongs to the room it is nearest.
const roomOf = (at: RouteWaypoint, rooms: RoomSplitRoom[]): RoomSplitRoom =>
  rooms.reduce((best, room) =>
    distanceToRoom(at, room) < distanceToRoom(at, best) ? room : best,
  );

const nearestOf = (
  from: RouteWaypoint,
  devices: RoomSplitDevice[],
): RoomSplitDevice | null =>
  devices.reduce<RoomSplitDevice | null>(
    (best, one) =>
      !best || distance(from, one.position) < distance(from, best.position)
        ? one
        : best,
    null,
  );

export const splitByRooms: SplitByRooms = ({
  devices,
  rooms,
  doors,
  maxPerGroup,
  boardPosition,
  pointsPerMetre,
}) => {
  const usable = rooms.filter((room) => room.points.length >= 3);
  if (usable.length === 0 || devices.length === 0) return null;
  const max = Math.max(1, Math.floor(maxPerGroup));

  const switched = devices.filter((device) => !device.isControl);
  const roomByDevice = new Map<string, RoomSplitRoom>();
  devices.forEach((device) => {
    // The switch outside the door belongs to the room whose lights it switches.
    const anchor = device.isControl
      ? (nearestOf(device.position, switched) ?? device)
      : device;
    roomByDevice.set(device.id, roomOf(anchor.position, usable));
  });

  const devicesByRoom = new Map<number, RoomSplitDevice[]>();
  devices.forEach((device) => {
    const room = roomByDevice.get(device.id)!;
    devicesByRoom.set(room.id, [...(devicesByRoom.get(room.id) ?? []), device]);
  });
  const occupied = usable.filter((room) => devicesByRoom.has(room.id));

  const reach = DOOR_REACH_M * pointsPerMetre;
  const neighbours = new Map<number, Set<number>>(
    occupied.map((room) => [room.id, new Set<number>()]),
  );
  doors.forEach(([a, b]) => {
    const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const touched = occupied.filter(
      (room) => distanceToRoom(middle, room) <= reach,
    );
    touched.forEach((one) =>
      touched.forEach(
        (other) => one.id !== other.id && neighbours.get(one.id)!.add(other.id),
      ),
    );
  });

  // Walk from the board, to a door-connected room where there is one, so a circuit's rooms sit together.
  const start = boardPosition ?? centreOf(occupied[0]);
  const order: RoomSplitRoom[] = [];
  const left = new Set(occupied.map((room) => room.id));
  let from = start;
  let current: RoomSplitRoom | null = null;
  while (left.size) {
    const here: RoomSplitRoom | null = current;
    const candidates = occupied.filter((room) => left.has(room.id));
    const adjacent: RoomSplitRoom[] = here
      ? candidates.filter((room) => neighbours.get(here.id)!.has(room.id))
      : [];
    const pool: RoomSplitRoom[] = adjacent.length ? adjacent : candidates;
    const origin = from;
    const next: RoomSplitRoom = pool.reduce((best, room) =>
      distance(origin, centreOf(room)) < distance(origin, centreOf(best))
        ? room
        : best,
    );
    order.push(next);
    left.delete(next.id);
    current = next;
    from = centreOf(next);
  }

  const groups: RoomSplitGroup[] = [];
  let open: RoomSplitGroup = { deviceIds: [], roomIds: [], roomNames: [] };
  const close = () => {
    if (open.deviceIds.length) groups.push(open);
    open = { deviceIds: [], roomIds: [], roomNames: [] };
  };
  order.forEach((room) => {
    const inRoom = devicesByRoom.get(room.id)!;
    // "Find the Schalter in the room, and put the lights and Schalter on the same Stromkreis."
    const hasOwnSwitch =
      inRoom.some((device) => device.isControl) &&
      inRoom.some((device) => !device.isControl);
    const isAlone =
      WET_ROOM.test(room.name) || hasOwnSwitch || inRoom.length > max;
    if (isAlone) {
      // A wet room, a room with its own switch or a full room is its own circuit; only a room past twice the size is divided, along its rows.
      close();
      const parts =
        inRoom.length > max * ROOM_DIVIDES_ABOVE
          ? splitIntoRows({ devices: inRoom, maxPerGroup: max })
          : [inRoom.map((device) => device.id)];
      parts.forEach((deviceIds) =>
        groups.push({ deviceIds, roomIds: [room.id], roomNames: [room.name] }),
      );
      return;
    }
    if (open.deviceIds.length + inRoom.length > max) close();
    open = {
      deviceIds: [...open.deviceIds, ...inRoom.map((device) => device.id)],
      roomIds: [...open.roomIds, room.id],
      roomNames: [...open.roomNames, room.name],
    };
  });
  close();
  return groups;
};
